import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Swal from "sweetalert2";
import api from "../services/api";
import "./Login.css";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const { login, setAuthToken, onboardingPending, onboardingPath } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Handle Google OAuth callback
    const token = searchParams.get("token");
    const error = searchParams.get("error");

    if (token) {
      (async () => {
        await setAuthToken(token);
        const userResponse = await api.get("/user");
        const redirectPath = userResponse.data?.onboarding?.path || userResponse.data?.user?.onboarding?.path || "/dashboard";

        Swal.fire({
          icon: "success",
          title: "Login Successful",
          showConfirmButton: false,
          timer: 1500,
        });

        navigate(redirectPath);
      })();
    }

    if (error) {
      Swal.fire({
        icon: "error",
        title: "Authentication Failed",
        text: decodeURIComponent(error),
      });
    }
  }, [searchParams, navigate, setAuthToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await login(email, password);
      Swal.fire({
        icon: "success",
        title: "Login Successful",
        text: "You are logged in.",
        showConfirmButton: false,
        timer: 1500,
      });
      navigate(response?.user?.onboarding?.path || (onboardingPending ? (onboardingPath || "/complete-registration") : "/dashboard"));
    } catch (error) {
      const message = error.response?.data?.message || "Login failed";
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: message,
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await api.get("/auth/google");
      // Redirect to Google OAuth URL
      window.location.href = response.data.url;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to initiate Google login",
      });
    }
  };

  const handleSendOtp = async () => {
    if (!forgotEmail) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please enter your email address",
      });
      return;
    }

    try {
      const response = await api.post("/forgot-password/send-otp", {
        email: forgotEmail,
      });

      Swal.fire({
        icon: "success",
        title: "OTP Sent",
        text: response.data.message,
        timer: 2000,
      });
      setOtpSent(true);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to send OTP",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!otp || !newPassword || !confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please fill all fields",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Passwords do not match",
      });
      return;
    }

    try {
      const response = await api.post("/forgot-password/verify-otp", {
        email: forgotEmail,
        otp: otp,
        password: newPassword,
        password_confirmation: confirmPassword,
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data.message,
      });

      // Reset form and close modal
      setShowForgotModal(false);
      setForgotEmail("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setOtpSent(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to reset password",
      });
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-card">
          <div className="logo-container">
            <img
              src="https://cedurnow.com/assets/img/logos/new-logo.png"
              alt="Logo"
              width="180"
            />
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="off"
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="off"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i
                    className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  ></i>
                </button>
              </div>
            </div>

            <div className="form-options">
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe">Remember me</label>
              </div>
              <div className="forgot-password">
                <button
                  type="button"
                  onClick={() => setShowForgotModal(true)}
                  className="forgot-link"
                >
                  <u>Forgot Password</u>
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-signin">
              Sign in
            </button>

            <hr className="divider" />

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="btn-google"
            >
              <img
                src="https://cedurnow.com/assets/img/google-signin.png"
                alt="Sign in with Google"
              />
            </button>
          </form>

          <div className="signup-link">
            <p>
              Don't have an account?{" "}
              <Link to="/register" className="sign-up-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowForgotModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Recover Password</h5>
              <button
                type="button"
                className="close-modal"
                onClick={() => setShowForgotModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Enter Email"
                />
                <div className="send-otp-link">
                  <button type="button" onClick={handleSendOtp}>
                    <u>Send OTP</u>
                  </button>
                </div>
              </div>

              {otpSent && (
                <>
                  <div className="form-group">
                    <label>
                      OTP <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter OTP"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      New Password <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter Password"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Confirm Password <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm Password"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResetPassword}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
